import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AtlasObjectType, atlasUrls, toAtlasRouterPath } from '@common/helpers/atlasUrls'
import { isInAtlasModuleFrame } from '../helpers/atlasModuleFrame'
import { isMessagesRoute } from '../helpers/messageNotifications'
import { openAtlasModulePanel } from '../redux/atlasModulePanel/actions'
import { IAtlasPreviewTarget } from '../redux/atlasPreview/actions'
import { useDispatch } from '../redux/hooks'
import { useIsMobile } from './useIsMobile'

export const taskPanelTarget = (taskId: string): IAtlasPreviewTarget => ({
  ref: { type: AtlasObjectType.Task, id: taskId },
  url: atlasUrls.task(taskId),
})

/**
 * Opens a task in a tab of its own: how AIM's floating chat window opens one, since a drawer
 * the window owned would close with it. Call it synchronously in the click handler, or the
 * browser blocks the tab.
 */
export const openTaskInNewTab = (taskId: string): void => {
  window.open(atlasUrls.task(taskId), '_blank', 'noopener,noreferrer')
}

/**
 * Opens a record in the split-screen panel beside AIM's conversations: the entry point for
 * any feature that shows a module next to messages. Where no panel can show (off AIM, on a
 * phone, inside the panel itself) the record opens as its page instead.
 */
export const useAtlasModulePanel = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isMobile = useIsMobile()
  const canOpenInPanel = isMessagesRoute(pathname) && !isMobile && !isInAtlasModuleFrame()

  const openInPanel = useCallback(
    (target: IAtlasPreviewTarget) => {
      if (canOpenInPanel) {
        dispatch(openAtlasModulePanel(target))
        return
      }
      void navigate(toAtlasRouterPath(target.url))
    },
    [canOpenInPanel, dispatch, navigate],
  )

  return { canOpenInPanel, openInPanel }
}
